import React, { useState, useEffect } from 'react';
import { Shield, Lock, Server, Key, Database, ArrowLeft } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

// Passed isMobile down so we can remove the heavy left-padding on phones
const SecuritySection = ({ icon, title, isMobile, children }) => (
  <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
      <div style={{ padding: '8px', background: '#eff6ff', borderRadius: '8px', color: '#2563eb', flexShrink: 0 }}>
        {icon}
      </div>
      <h3 style={{ fontSize: isMobile ? '1.15rem' : '1.25rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{title}</h3>
    </div>
    <div style={{ 
      paddingLeft: isMobile ? '0' : '52px', // Drops the indent on mobile so text isn't squished
      color: '#475569', 
      lineHeight: '1.7', 
      fontSize: isMobile ? '0.95rem' : '1rem' 
    }}>
      {children}
    </div>
  </div>
);

const Security = () => {
  const navigate = useNavigate();
  
  // --- ADDED MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const theme = {
    primary: '#3ba9e1',     
    primaryDark: '#2c8ac0', 
    secondary: '#0f172a',
    navBg: '#132238',       
    navText: '#ffffff',     
    accent: '#f59e0b',      
    success: '#10b981',     
    bgLight: '#f8fafc',     
    bgWhite: '#ffffff',
  };

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar theme={theme} />

      <div style={{ 
        flex: 1,
        padding: isMobile ? '100px 20px 40px' : '120px 20px 80px', 
        maxWidth: '800px', 
        margin: '0 auto', 
        width: '100%',
        boxSizing: 'border-box' // <-- Prevents horizontal scrolling
      }}>
        
        <button 
          onClick={() => navigate('/')}
          style={{ 
            background: 'none', border: 'none', cursor: 'pointer', 
            display: 'flex', alignItems: 'center', gap: '8px', 
            color: '#64748b', marginBottom: '40px', fontWeight: '500', fontSize: '0.9rem'
          }}
        >
          <ArrowLeft size={16} /> Back to Home
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? '50px' : '80px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '30px', background: '#dcfce7', color: '#166534', fontWeight: '600', fontSize: '0.9rem', marginBottom: '30px' }}>
            <Shield size={16} /> Industry Standard Security
          </div>
          <h1 style={{ fontSize: isMobile ? '2.2rem' : '3rem', fontWeight: '800', color: '#0f172a', marginBottom: '20px', lineHeight: '1.1' }}>
            Built on secure foundations.
          </h1>
          <p style={{ fontSize: isMobile ? '1.05rem' : '1.2rem', color: '#64748b', lineHeight: '1.6' }}>
            We utilize Microsoft Azure infrastructure and NIST-compliant standards to ensure your project data is isolated, encrypted, and available.
          </p>
        </div>

        {/* Core Pillars */}
        <div style={{ 
          backgroundColor: '#f8fafc', 
          padding: isMobile ? '25px' : '40px', // Shrinks padding on mobile
          borderRadius: '20px', 
          border: '1px solid #e2e8f0', 
          marginBottom: '60px',
          boxSizing: 'border-box' // <-- Keeps inner content contained
        }}>
            
            <SecuritySection isMobile={isMobile} icon={<Lock size={24} />} title="Encryption Standards">
              <p><strong>At Rest:</strong> All customer data is encrypted using AES-256 via Azure Storage & Database Encryption.</p>
              <p style={{marginTop: '10px'}}><strong>In Transit:</strong> All data transmitted between your devices and our servers is secured via TLS 1.2+ protocols to prevent interception.</p>
            </SecuritySection>

            <SecuritySection isMobile={isMobile} icon={<Key size={24} />} title="Authentication & Passwords">
              <p>We use industry-standard <strong>JWT (JSON Web Tokens)</strong> with automatic rotation mechanisms to prevent session hijacking.</p>
              <p style={{marginTop: '10px'}}>User passwords are never stored in plain text. They are irreversibly hashed using <strong>PBKDF2 with SHA-256</strong>, meeting NIST compliance standards.</p>
            </SecuritySection>

            <SecuritySection isMobile={isMobile} icon={<Database size={24} />} title="Data Isolation & Multi-Tenancy">
              <p>We utilize logical multi-tenancy with strict <strong>Row-Level Security (RLS)</strong> enforcement at the database layer. This ensures that no customer can ever access another tenant's data, regardless of the application logic.</p>
            </SecuritySection>

            <SecuritySection isMobile={isMobile} icon={<Server size={24} />} title="Cloud Infrastructure">
              <p>SiteACT is hosted on <strong>Microsoft Azure</strong>, benefiting from Microsoft's world-class physical security, redundancy, and ISO 27001 / SOC 2 certifications.</p>
            </SecuritySection>

        </div>

        {/* CTA for IT Teams */}
        <div style={{ textAlign: 'center', borderTop: '1px solid #e2e8f0', paddingTop: isMobile ? '40px' : '60px' }}>
          <h3 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '15px' }}>Need a Vendor Risk Assessment?</h3>
          <p style={{ color: '#64748b', marginBottom: '30px', fontSize: isMobile ? '0.95rem' : '1rem' }}>
            Our technical team is available to complete your security questionnaires.
          </p>
          <a href="mailto:security@siteact.app" style={{ 
            display: 'inline-block', padding: '12px 24px', backgroundColor: '#0f172a', color: 'white', 
            borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold',
            width: isMobile ? '100%' : 'auto', // Button spans full width on phone
            boxSizing: 'border-box'
          }}>
            Contact Security Team
          </a>
        </div>

      </div>

      <Footer theme={theme} />
    </div>
  );
};

export default Security;